import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { CircularProgress } from "@mui/material";
import { firestore } from "../services/firebase";
import { THEME_MAP } from "../theme";
import { captureError } from "../lib/errorReporting";

export function HowTo() {
    const [howToItems, setHowToItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const fetchHowToItems = async () => {
            try {
                const snapshot = await firestore
                    .collection("HowTo")
                    .orderBy("order", "asc")
                    .get();
                
                const items = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                
                setHowToItems(items);
            } catch (error) {
                captureError(error, { view: "HowTo", action: "fetchHowToItems" });
                console.error("Error fetching How To items:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHowToItems();
    }, []);

    if (isLoading) {
        return (
            <Container>
                <LoadingContainer>
                    <CircularProgress />
                </LoadingContainer>
            </Container>
        );
    }

    if (howToItems.length === 0) {
        return (
            <Container>
                <NoContentContainer>
                    <h2>No How-To content available yet.</h2>
                    <p>Check back later for helpful guides!</p>
                </NoContentContainer>
            </Container>
        );
    }

    const currentItem = howToItems[currentIndex];

    return (
        <Container>
            <HowToContainer>
                <NavigationContainer>
                    <StepIndicator>
                        Step {currentIndex + 1} of {howToItems.length}
                    </StepIndicator>
                </NavigationContainer>

                <ContentContainer>
                    <ImageContainer>
                        {currentItem.imageUrl ? (
                            <img 
                                src={currentItem.imageUrl} 
                                alt={currentItem.title || `Step ${currentIndex + 1}`}
                            />
                        ) : (
                            <PlaceholderImage>
                                <span>No image available</span>
                            </PlaceholderImage>
                        )}
                    </ImageContainer>

                    <TextContainer>
                        {currentItem.title && <h2>{currentItem.title}</h2>}
                        <p>{currentItem.description || "No description available."}</p>
                    </TextContainer>
                </ContentContainer>

                <ButtonContainer>
                    <NavigationButton 
                        onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                        disabled={currentIndex === 0}
                    >
                        Previous
                    </NavigationButton>
                    
                    <NavigationButton 
                        onClick={() => setCurrentIndex(Math.min(howToItems.length - 1, currentIndex + 1))}
                        disabled={currentIndex === howToItems.length - 1}
                        primary
                    >
                        Next
                    </NavigationButton>
                </ButtonContainer>

                <StepDots>
                    {howToItems.map((_, index) => (
                        <Dot 
                            key={index}
                            active={index === currentIndex}
                            onClick={() => setCurrentIndex(index)}
                        />
                    ))}
                </StepDots>
            </HowToContainer>
        </Container>
    );
}

const Container = styled.div`
    height: 100%;
    width: 100%;
    padding: 24px;
    overflow: auto;
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 300px;
`;

const NoContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 300px;
    text-align: center;
    color: ${THEME_MAP["color-basic-600"]};
`;

const HowToContainer = styled.div`
    max-width: 800px;
    margin: 0 auto;
    background: ${THEME_MAP["color-basic-100"]};
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const NavigationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    h1 {
        color: ${THEME_MAP["color-primary-500"]};
        margin: 0;
    }
`;

const StepIndicator = styled.div`
    color: ${THEME_MAP["color-basic-600"]};
    font-weight: 500;
`;

const ContentContainer = styled.div`
    margin-bottom: 32px;
`;

const ImageContainer = styled.div`
    width: 100%;
    height: 400px;
    border: 2px solid ${THEME_MAP["color-basic-300"]};
    border-radius: 8px;
    margin-bottom: 16px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${THEME_MAP["color-basic-200"]};

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const PlaceholderImage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: ${THEME_MAP["color-basic-600"]};
    font-size: 18px;
`;

const TextContainer = styled.div`
    h2 {
        color: ${THEME_MAP["color-primary-500"]};
        margin-bottom: 12px;
    }

    p {
        color: ${THEME_MAP["color-basic-700"]};
        line-height: 1.6;
        font-size: 16px;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 24px;
`;

const NavigationButton = styled.button`
    padding: 12px 24px;
    border: 1px solid ${THEME_MAP["color-primary-500"]};
    border-radius: 4px;
    background: ${props => props.primary ? THEME_MAP["color-primary-500"] : "transparent"};
    color: ${props => props.primary ? THEME_MAP["color-basic-100"] : THEME_MAP["color-primary-500"]};
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        background: ${THEME_MAP["color-primary-500"]};
        color: ${THEME_MAP["color-basic-100"]};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const StepDots = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
`;

const Dot = styled.div`
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => props.active ? THEME_MAP["color-primary-500"] : THEME_MAP["color-basic-400"]};
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
        background: ${THEME_MAP["color-primary-500"]};
    }
`;